import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PatientDetail } from "@/hooks/usePatientDetail";
import { format, parseISO, differenceInYears } from "date-fns";
import { User, Phone, Mail, MapPin, AlertCircle, Calendar, Heart } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface PatientSummaryCardProps {
  patient: PatientDetail;
}

export function PatientSummaryCard({ patient }: PatientSummaryCardProps) {
  // Calculate age from date of birth
  const calculateAge = (dateOfBirth: string | undefined): number | null => {
    if (!dateOfBirth) return null;
    try {
      const dob = parseISO(dateOfBirth);
      return differenceInYears(new Date(), dob);
    } catch (e) {
      console.error("Error calculating age:", e);
      return null;
    }
  };

  const age = calculateAge(patient.date_of_birth);

  // Format date
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return "Not provided";
    try {
      return format(parseISO(dateString), "MMM d, yyyy");
    } catch (e) {
      return dateString || "Not provided";
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Patient Summary</CardTitle>
        <CardDescription>Basic patient information</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Basic Information */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-gray-500" />
            <span className="font-medium">
              {patient.first_name} {patient.last_name}
            </span>
            {age !== null && (
              <Badge variant="outline" className="ml-auto">
                {age} years old
              </Badge>
            )}
          </div>
          
          {patient.gender && (
            <div className="flex items-center gap-2">
              <div className="h-4 w-4" /> {/* Spacer for alignment */}
              <span className="text-sm text-gray-600">
                {patient.gender.charAt(0).toUpperCase() + patient.gender.slice(1)}
              </span>
            </div>
          )}
          
          {patient.date_of_birth && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">
                Born {formatDate(patient.date_of_birth)}
              </span>
            </div>
          )}
        </div>
        
        {/* Contact Information */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Contact Information</h3>
          
          {patient.email && (
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">{patient.email}</span>
            </div>
          )}
          
          {patient.phone && (
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">{patient.phone}</span>
            </div>
          )}
          
          {patient.address && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">{patient.address}</span>
            </div>
          )}
        </div>
        
        {/* Emergency Contact */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Emergency Contact</h3>
          
          {(patient.emergency_contact_name || patient.emergency_contact_phone) ? (
            <>
              {patient.emergency_contact_name && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">{patient.emergency_contact_name}</span>
                </div>
              )}
              
              {patient.emergency_contact_phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">{patient.emergency_contact_phone}</span>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <span className="text-sm text-gray-600">No emergency contact provided</span>
            </div>
          )}
        </div>
        
        {/* Medical Information */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Medical Information</h3>
          
          <div className="flex items-start gap-2">
            <Heart className="h-4 w-4 text-gray-500 mt-0.5" />
            <div>
              <span className="text-sm font-medium text-gray-700">Medical History</span>
              <p className="text-sm text-gray-600">
                {patient.medical_history || "No medical history recorded"}
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-gray-500 mt-0.5" />
            <div>
              <span className="text-sm font-medium text-gray-700">Allergies</span>
              <p className="text-sm text-gray-600">
                {patient.allergies || "No allergies recorded"}
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-2">
            <div className="rounded-full bg-blue-100 p-1">
              <svg className="h-2 w-2 text-blue-500" fill="currentColor" viewBox="0 0 8 8">
                <circle cx="4" cy="4" r="3" />
              </svg>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Medications</span>
              <p className="text-sm text-gray-600">
                {patient.medications || "No medications recorded"}
              </p>
            </div>
          </div>
        </div>
        
        {/* Patient Since */}
        <div className="pt-2 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            Patient since {formatDate(patient.created_at)}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
